import { useState, useEffect, FC } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Field, Formik, FormikHelpers } from 'formik'
import { Format } from 'services/Dates'

import useApi from 'components/Hooks/useApi'
import { refreshCounter } from 'store/actions/refresh'
import { createErrorNotification } from 'store/actions/notifications'
import CommentsHistoryModal from 'components/Comments/CommentsHistoryModal'
import { DeleteButton, ProfileImage } from 'components/UI'
import { Button, Form } from 'components/Forms'
import { Button as MerinioButton, Spinner } from 'ui'

import { t } from 'translations'
import styles from './comments.module.scss'

type Props = {
  resourceId: string
  resourceType: 'vacation' | 'leave' | 'user'
}

type Comment = MData.Comment & { user: { _id: string; firstName: string; lastName: string } }

type CommentsResponse = Record<string, { _id: string; comments: Comment[] }>

const Comments: FC<Props> = ({ resourceId, resourceType }) => {
  const dispatch = useDispatch()
  // Get the connected user
  const userInfo = useSelector((state: MStore.RootState) => state.auth.decodedToken)

  const { usersById } = useSelector((state: MStore.RootState) => state.resources)
  const userId = userInfo?.uid

  const { defaultTimeZone: timeZone } = useSelector((state: MStore.RootState) => state.settings)

  // Define all queries
  const { handleApi: fetchAllComments, isLoading } = useApi({ method: 'GET', url: 'comments/comments', autoExecute: false })
  const { handleApi: addCommentHandler } = useApi({ method: 'POST', url: 'comments/comments' })
  const { handleApi: removeCommenthandler } = useApi({ method: 'DELETE', url: 'comments/comments' })
  const { handleApi: editCommentHandler } = useApi({ method: 'PUT', url: 'comments/comments' })

  const [comments, setComments] = useState<Comment[]>([])
  const [commentInEditMode, setCommentInEditMode] = useState<MData.Comment | Record<string, never>>({})
  const [commentHistoryModal, setCommentHistoryModal] = useState<MData.Comment | Record<string, never>>({})

  useEffect(() => {
    // Function to retrieve all the comments that match the provided resource id
    async function getAllComments() {
      const { response, isError }: { response: CommentsResponse | null; isError: boolean } = await fetchAllComments({
        params: { resourceIds: resourceId, resourceType, group: true }
      })
      if (isError) return dispatch(createErrorNotification())
      if (!response) return
      const commentsResponse = response[resourceId]?.comments ?? []
      setComments(commentsResponse)
    }
    getAllComments()
  }, [resourceId, resourceType, fetchAllComments, dispatch])

  const isAuthor = (comment: Comment) => userId === comment.createdBy

  // Function to open the history modal
  const showHistoryModal = (comment: Comment) => setCommentHistoryModal(comment)

  // Callback passed to Modal to close it when clicking its Close button
  const closeHistoryModal = () => setCommentHistoryModal({})

  const addComment = async (values: { myComment: string; commentEditing: string }) => {
    // Build the comment
    const comment = {
      resourceId,
      resourceType,
      text: values.myComment.trim()
    }
    // Save the comment to the db
    const { response, isError }: { response: Comment | null; isError: boolean } = await addCommentHandler(comment)

    if (isError) return dispatch(createErrorNotification())
    if (!response) return
    const newComments = [...comments, response]
    setComments(newComments)
    //Refresh the counter depending on the resourceType
    if (resourceType === 'leave') dispatch(refreshCounter('attendances'))
    if (resourceType === 'vacation') dispatch(refreshCounter('timeOffs'))
    if (resourceType === 'user') dispatch(refreshCounter('users'))
  }

  const removeComment = async (commentId: string) => {
    // Delete the comment from the db
    const { isError } = await removeCommenthandler({ params: { _id: commentId, resourceType } })

    if (isError) return dispatch(createErrorNotification())
    const newComments = comments.filter(comment => comment._id !== commentId)
    setComments(newComments)
    //Refresh the counter depending on the resourceType
    if (resourceType === 'leave') dispatch(refreshCounter('attendances'))
    if (resourceType === 'vacation') dispatch(refreshCounter('timeOffs'))
    if (resourceType === 'user') dispatch(refreshCounter('users'))
  }

  const editComment = async (comment: Comment, editedText: string) => {
    // Update the comment with the new text
    comment.text = editedText.trim()

    // Update the comment to the db
    const { response, isError }: { response: Comment | null; isError: boolean } = await editCommentHandler({ ...comment, resourceType })
    if (isError) return dispatch(createErrorNotification())

    // Turn off editing mode for that comment after the edit
    toggleEditComment(comment)

    const newComments = comments.map(comment => (comment._id === response?._id ? response : comment))
    setComments(newComments)
  }

  const isInEditMode = (comment: Comment) => commentInEditMode && commentInEditMode._id === comment._id

  // Turn on or off the edit mode of the provided comment
  const toggleEditComment = (comment: Comment) => {
    // Turn off
    if (isInEditMode(comment)) setCommentInEditMode({})
    // Turn on
    else setCommentInEditMode(comment)
  }

  // Function that returns the buttons to 'Edit' and 'Remove' comment
  const authorButtons = (comment: Comment, setFieldValue: (field: string, value: string, shouldValidate?: boolean) => void) => {
    return (
      // Add buttons only if the user is the owner of that comment
      isAuthor(comment) && (
        <span>
          <strong> &middot; </strong>
          {!isInEditMode(comment) && (
            <span
              className="btn btn-link text-dark"
              onClick={() => {
                setFieldValue('commentEditing', comment.text)
                toggleEditComment(comment)
              }}
            >
              {t('actions.EDIT')}
            </span>
          )}
          {isInEditMode(comment) && (
            <span className="btn btn-link text-dark" onClick={() => toggleEditComment(comment)}>
              {t('actions.CANCEL')}
            </span>
          )}
          <strong> &middot; </strong>
          <span>
            <DeleteButton
              buttonClass="btn btn-link text-danger"
              onClick={() => removeComment(comment._id)}
              title={'components.comments.DELETE_BUTTON'}
              disableClose
            />
          </span>
        </span>
      )
    )
  }

  const historyButton = (comment: Comment) => {
    // Add only if there is more than one diff to that comment (other than the creation diff)
    return comment.diffs && comment.diffs.length > 1 ? (
      <div>
        <MerinioButton
          isLink
          color="info"
          size="small"
          onClick={e => {
            e.preventDefault()
            showHistoryModal(comment)
          }}
        >
          {t('components.comments.SHOW_EDITS')}
        </MerinioButton>
      </div>
    ) : null
  }

  const displayComment = (comment: Comment, values: { myComment: string; commentEditing: string }, isSubmitting) => {
    // If in editing mode, display the textarea field
    return isInEditMode(comment) ? (
      <div className="input-group pb-3">
        <Field name="commentEditing" className="form-control p-2" component="textarea"></Field>
        <div className="input-group-append">
          <Button
            type="button"
            className="btn btn-outline-secondary"
            isSubmitting={isSubmitting}
            disabled={!values.commentEditing || comment.text === values.commentEditing || values.commentEditing.trim() === comment.text}
            onClick={() => {
              editComment(comment, values.commentEditing)
            }}
          >
            {t('actions.EDIT')}
          </Button>
        </div>
      </div>
    ) : (
      // Else just display the text
      <p className={styles['comment-container']}>{comment.text}</p>
    )
  }

  const onSubmit = (values: { myComment: string; commentEditing: string }, actions: FormikHelpers<{ myComment: string; commentEditing: string }>) => {
    addComment(values)
    actions.setFieldValue('myComment', '')
    actions.setSubmitting(false)
  }

  if (isLoading) return <Spinner size="lg" />

  return (
    <>
      {commentHistoryModal._id ? <CommentsHistoryModal comment={commentHistoryModal} closeModal={closeHistoryModal} timeZone={timeZone} /> : null}
      <Formik enableReinitialize={true} initialValues={{ myComment: '', commentEditing: '' }} onSubmit={(values, actions) => onSubmit(values, actions)}>
        {({ values, setFieldValue, dirty, isSubmitting, submitForm, ...actions }) => {
          return (
            <>
              <Form resetForm={actions.resetForm} noPrompt>
                {() => (
                  <div className="text-preline">
                    {!comments.length && (
                      <p>
                        <i>{t('components.comments.NO_COMMENTS')}</i>
                      </p>
                    )}
                    {comments.map(comment => {
                      const userInfo = usersById[comment.createdBy]
                      const { firstName, lastName, picture } = userInfo

                      return (
                        <div key={comment._id} className="row comment-container">
                          <div className="col-12 d-flex flex-row">
                            <ProfileImage firstName={firstName} lastName={lastName} pictureUrl={picture?.url ?? null} className="mr-2" />
                            <div className="flex-fill">
                              <div>
                                <h6 className="d-inline">
                                  <strong>{`${comment.user.firstName} ${comment.user.lastName}`}</strong>
                                </h6>
                                {authorButtons(comment, setFieldValue)}
                              </div>
                              <div>
                                <small>{Format(comment.createdDate, { isUTC: true, timeZone, outFormat: 'distance' })}</small>
                              </div>
                              {historyButton(comment)}
                              {displayComment(comment, values, isSubmitting)}
                            </div>
                          </div>
                          <hr style={{ width: '98%' }} className={styles['comment-separator']} />
                        </div>
                      )
                    })}
                    {/* Add a new comment textarea */}
                    <div className="input-group">
                      <Field
                        className="form-control p-2"
                        placeholder={t('components.comments.INPUT_COMMENT_PLACEHOLDER')}
                        name="myComment"
                        component="textarea"
                        onChange={({ target }) =>
                          target.value.trim() ? setFieldValue('myComment', target.value) : setFieldValue('myComment', target.value.trim())
                        }
                      />
                      {/* Submit comment button */}
                      <div className="input-group-append">
                        <Button
                          type="button"
                          onClick={submitForm}
                          className="btn btn-outline-secondary"
                          isSubmitting={isSubmitting}
                          text={t('actions.SEND')}
                          disabled={!values.myComment || !values.myComment.trim()}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </Form>
            </>
          )
        }}
      </Formik>
    </>
  )
}

export default Comments
